import { useState, useEffect, useRef, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { navigationStateFrom } from "@/types/navigation";
import { SearchLg, LayoutGrid01, List, Edit01, Move, Share01, Trash01, Folder, Plus, Download01, Upload01, Zap, RefreshCw01, Clock, Package, XClose } from "@untitledui/icons";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Tabs } from "@/components/application/tabs/tabs";
import { DocumentList, type FolderSortField, type DocumentSortField, type SortDirection } from "@/components/application/documents/document-list";
import { Button } from "@/components/base/buttons/button";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import type { DocumentFile, UnmappedFile, ViewMode, BreadcrumbItem } from "@/types/documents";
import type { Container } from "@/types/container";
import { useContainerTable } from "@/hooks/api/use-containers-recap";
import type { CustomFolder } from "@/types/documents";
import {
    useFolders,
    useFolder,
    useCreateFolder,
    useUpdateFolder,
    useDeleteFolder,
    useMoveFolder,
    useFolderDocuments,
    useAssociateDocumentsToFolder,
    useRemoveDocumentFromFolder,
    useUnmappedDocuments,
    useGlobalSearch,
} from "@/hooks/api/use-folders";
import { useDeleteDocument, useExtractDocument, useUpdateDocument } from "@/hooks/api/use-documents";
import { useCreateDocumentVersion } from "@/hooks/api/use-document-versions";
import { useDocumentTypes } from "@/hooks/api/use-document-types";
import { Tag01 } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast-service";
import { FOLDERS_QUERY_KEYS } from "@/hooks/api/use-folders";
import { ChevronRight } from "@untitledui/icons";
import { storage } from "@/lib/local-storage";
import { useFolderPreferences } from "@/hooks/use-folder-preferences";
import { ContextMenu, type ContextMenuItem } from "@/components/application/documents/context-menu";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { FileUploadDropZone } from "@/components/application/file-upload/file-upload-base";
import { FileUploadPreviewInline } from "@/components/application/documents/file-upload-preview-inline";
import type { UploadFileItem } from "@/components/application/documents/file-upload-preview-modal";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";

export const ShipperDocumentsPage = () => {
    const { t } = useTranslation("pages:documents");
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // Folder preferences hook - persists state to localStorage
    const folderPrefs = useFolderPreferences();

    // Initialize state from URL params first, then fall back to stored preferences
    const [activeTab, setActiveTab] = useState<"myDrive" | "unmapped">(() => {
        const urlTab = searchParams.get("tab") as "myDrive" | "unmapped";
        return urlTab || folderPrefs.lastTab;
    });
    const [viewMode, setViewMode] = useState<ViewMode>(() => {
        const urlView = searchParams.get("view") as ViewMode;
        return urlView || folderPrefs.viewMode;
    });
    const [showEmptyFolders, setShowEmptyFolders] = useState<boolean>(
        folderPrefs.showEmptyFolders
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<Array<{
        type: "document" | "unmapped_file" | "container" | "custom_folder";
        item: DocumentFile | UnmappedFile | Container | CustomFolder;
    }>>([]);
    // Search results by type for sectioned display (Cartelle, Documenti, Container)
    const [searchSectionFolders, setSearchSectionFolders] = useState<{ containers: Container[]; customFolders: CustomFolder[] }>({ containers: [], customFolders: [] });
    const [searchSectionDocuments, setSearchSectionDocuments] = useState<DocumentFile[]>([]);
    const [searchSectionContainers, setSearchSectionContainers] = useState<Container[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Navigation state - use URL params first, then stored preferences
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(() => {
        const urlFolder = searchParams.get("folder");
        return urlFolder || folderPrefs.lastFolderId;
    });
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

    // Sync state from URL when searchParams change (e.g. initial load with full URL or browser back/forward).
    // Fixes empty My Drive when opening a link with ?tab=myDrive&folder=... where initial state
    // might have been set from localStorage before URL was ready.
    useEffect(() => {
        const urlTab = searchParams.get("tab") as "myDrive" | "unmapped" | null;
        const urlView = searchParams.get("view") as ViewMode | null;
        const urlFolder = searchParams.get("folder");
        if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
        if (urlView && urlView !== viewMode) setViewMode(urlView);
        if (urlFolder !== undefined && urlFolder !== currentFolderId) setCurrentFolderId(urlFolder || null);
    }, [searchParams]);

    // Handle containerId param - navigate to container's folder
    const urlContainerId = searchParams.get("containerId");

    // Data
    const [containers, setContainers] = useState<Container[]>([]);
    const [customFolders, setCustomFolders] = useState<CustomFolder[]>([]);
    const [documents, setDocuments] = useState<DocumentFile[]>([]);
    const [unmappedFiles, setUnmappedFiles] = useState<UnmappedFile[]>([]);

    // Context Menu state
    const [contextMenu, setContextMenu] = useState<{
        isOpen: boolean;
        position: { x: number; y: number } | null;
        items: ContextMenuItem[];
    }>({
        isOpen: false,
        position: null,
        items: [],
    });

    // Modals state
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showClassifyModal, setShowClassifyModal] = useState(false);
    const [showCreateVersionModal, setShowCreateVersionModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentFile | null>(null);
    const [selectedCustomFolder, setSelectedCustomFolder] = useState<CustomFolder | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [newFolderName, setNewFolderName] = useState("");
    const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
    const [selectedContainerId, setSelectedContainerId] = useState("");
    const [selectedParentFolderId, setSelectedParentFolderId] = useState<string | null>(null);
    const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
    const [inlineNewFolderName, setInlineNewFolderName] = useState("");
    const [isCreatingInlineFolder, setIsCreatingInlineFolder] = useState(false);
    const [isMovingFolder, setIsMovingFolder] = useState(false);
    const [showDeleteFoldersModal, setShowDeleteFoldersModal] = useState(false);
    const [isDeletingFolders, setIsDeletingFolders] = useState(false);

    // File upload ref
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [versionFile, setVersionFile] = useState<File | null>(null);

    // Use react-dropzone for version file upload in modal
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        noClick: true, // Don't open file dialog on click (we handle it manually)
        noKeyboard: true, // Don't open file dialog on keyboard
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setVersionFile(acceptedFiles[0]);
            }
        },
        accept: {
            'application/pdf': ['.pdf'],
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        disabled: !showCreateVersionModal,
    });

    // Add global event listeners to catch ALL drag events before browser opens file
    useEffect(() => {
        if (!showCreateVersionModal) return;

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = 'copy';
            }
        };

        const handleDrop = (e: DragEvent) => {
            // Only prevent default to stop browser from opening file
            // DON'T stop propagation - let react-dropzone handle it
            e.preventDefault();
        };

        window.addEventListener('dragover', handleDragOver, true);
        window.addEventListener('drop', handleDrop, true);

        return () => {
            window.removeEventListener('dragover', handleDragOver, true);
            window.removeEventListener('drop', handleDrop, true);
        };
    }, [showCreateVersionModal]);

    // Upload state
    const [selectedFiles, setSelectedFiles] = useState<UploadFileItem[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    // Bulk selection state for documents
    const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());

    // Bulk selection state for folders
    const [selectedFolderIds, setSelectedFolderIds] = useState<Set<string>>(new Set());

    // Folder sorting state - uses preferences hook for persistence
    const [folderSortField, setFolderSortField] = useState<FolderSortField>(
        folderPrefs.sortField
    );
    const [folderSortDirection, setFolderSortDirection] = useState<SortDirection>(
        folderPrefs.sortDirection
    );

    // Handle folder sorting
    const handleFolderSort = (field: FolderSortField) => {
        if (folderSortField === field) {
            // Toggle direction if same field
            const newDirection = folderSortDirection === "asc" ? "desc" : "asc";
            setFolderSortDirection(newDirection);
            folderPrefs.setSortDirection(newDirection);
        } else {
            // New field, default to desc (most recent first)
            setFolderSortField(field);
            setFolderSortDirection("desc");
            folderPrefs.setSortField(field);
            folderPrefs.setSortDirection("desc");
        }
    };

    // Document sorting state
    const [documentSortField, setDocumentSortField] = useState<DocumentSortField>("uploadedAt");
    const [documentSortDirection, setDocumentSortDirection] = useState<SortDirection>("desc");

    // Handle document sorting
    const handleDocumentSort = (field: DocumentSortField) => {
        if (documentSortField === field) {
            // Toggle direction if same field
            setDocumentSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            // New field, default to desc (most recent first)
            setDocumentSortField(field);
            setDocumentSortDirection("desc");
        }
    };

    // API Hooks
    // Load folders: if currentFolderId, load children; otherwise load root folders
    // show_empty: true ensures container folders without documents are visible
    const { data: foldersData, isLoading: foldersLoading } = useFolders(
        currentFolderId
            ? { parent_id: currentFolderId, show_empty: true }
            : { parent_id: null, show_empty: true }
    );
    // Load all folders for root level (to get both custom and container folders)
    const { data: allFoldersData } = useFolders({ parent_id: null, show_empty: true });
    const { data: folderDetail, isLoading: folderDetailLoading } = useFolder(
        currentFolderId || "",
        { enabled: !!currentFolderId }
    );
    const { data: folderDocuments } = useFolderDocuments(
        currentFolderId || "",
        { enabled: !!currentFolderId && !!folderDetail }
    );
    const { data: unmappedDocumentsData } = useUnmappedDocuments();

    // Global search hook - debounced via query key
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const { data: searchData, isLoading: isSearchLoading } = useGlobalSearch(debouncedSearchQuery, {
        enabled: debouncedSearchQuery.length > 0,
    });

    // Document types for manual classification + name resolution
    const { data: documentTypesData } = useDocumentTypes();
    const docTypeNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if (documentTypesData) {
            for (const dt of documentTypesData) {
                map.set(dt.document_type_id, dt.name);
            }
        }
        return map;
    }, [documentTypesData]);

    // Mutations
    const createFolderMutation = useCreateFolder();
    const updateFolderMutation = useUpdateFolder();
    const deleteFolderMutation = useDeleteFolder();
    const moveFolderMutation = useMoveFolder();
    const deleteDocumentMutation = useDeleteDocument();
    const extractDocumentMutation = useExtractDocument();
    const updateDocumentMutation = useUpdateDocument();
    const removeDocumentFromFolderMutation = useRemoveDocumentFromFolder();
    const associateDocumentsMutation = useAssociateDocumentsToFolder();
    const createVersionMutation = useCreateDocumentVersion();

    // Fetch real containers for the "Associate to Container" modal
    const { data: containersTableData } = useContainerTable({
        startDate: null,
        endDate: null,
        skip: 0,
        limit: 200,
        enabled: showMoveModal,
    });
    const containerSelectItems = useMemo(() => {
        if (!containersTableData?.containers) return [];
        return containersTableData.containers
            .filter(c => c.container_number)
            .map(c => ({ id: c.container_id, label: c.container_number || 'Unknown' }));
    }, [containersTableData]);

    // Load data based on current folder
    useEffect(() => {
        if (activeTab === "myDrive") {
            if (currentFolderId && folderDetail) {
                // We have a folder detail - could be custom or container folder
                if (folderDetail.folder_type === "custom") {
                    // Custom folder: show nested folders, containers, and documents
                    const customFoldersList = foldersData?.filter(f => f.folder_type === "custom") || [];
                    setCustomFolders(customFoldersList.map(f => ({
                        id: f.folder_id,
                        name: f.name || "",
                        parentId: f.parent_id,
                        containerIds: folderDetail.containers.map(c => c.container_id),
                        createdAt: new Date(f.created_at),
                        updatedAt: new Date(f.updated_at),
                    })));

                    // Get containers: combine folder detail containers + child container folders
                    // folderDetail.containers = containers linked via FolderContainer (id = container_id)
                    // foldersData container types = container folders moved under this custom folder (id = folder_id)
                    // IMPORTANT: Always use folder_id as the id for navigation and move operations
                    const linkedContainers: Container[] = folderDetail.containers.map(c => {
                        // Look up the folder_id for this container from allFoldersData
                        const containerFolder = allFoldersData?.find(
                            f => f.folder_type === "container" && f.container_id === c.container_id
                        );
                        return {
                            id: containerFolder?.folder_id ?? c.container_id, // prefer folder_id
                            containerNumber: c.container_number,
                            status: c.status || "draft",
                            createdAt: containerFolder ? new Date(containerFolder.created_at) : undefined,
                            updatedAt: containerFolder ? new Date(containerFolder.updated_at) : undefined,
                        } as Container;
                    });

                    const childContainerFolders: Container[] = (foldersData?.filter(f => f.folder_type === "container") || [])
                        .filter(f => f.container_id !== null)
                        .map(f => ({
                            id: f.folder_id,
                            containerNumber: f.container_number || `Container ${f.container_id!.slice(0, 8)}`,
                            status: "In Transit" as const,
                            createdAt: new Date(f.created_at),
                            updatedAt: new Date(f.updated_at),
                        } as Container));

                    // Merge, dedup by id (folder_id)
                    const containerMap = new Map<string, Container>();
                    for (const c of linkedContainers) containerMap.set(c.id, c);
                    for (const c of childContainerFolders) containerMap.set(c.id, c);
                    setContainers(Array.from(containerMap.values()));

                    // Convert API documents to DocumentFile format for custom folder
                    // Note: documents in custom folders may not have container_id (unmapped)
                    const docs: DocumentFile[] = folderDocuments?.map(doc => ({
                        id: doc.document_id,
                        containerId: null, // Documents in custom folders may be unmapped
                        type: (doc.document_type as any) || "unknown",
                        typeName: doc.document_type ? docTypeNameMap.get(doc.document_type) : undefined,
                        name: doc.file_name || "",
                        uploadedAt: new Date(doc.upload_timestamp),
                        updatedAt: doc.updated_at ? new Date(doc.updated_at) : undefined,
                        uploadSource: doc.upload_source || undefined,
                        fileUrl: `/documents/${doc.document_id}/download`,
                        fileSize: 0, // Not available in API response
                        mimeType: doc.file_mime_type || "",
                        extractedData: {},
                        missingFields: [],
                        status: doc.extraction_status === "extracted" ? "Completo" : "Pending",
                        extractionStatus: (doc.extraction_status as any) || undefined,
                        validationStatus: (doc.validation_status as DocumentFile["validationStatus"]) ?? undefined,
                    })) || [];
                    setDocuments(docs);

                    // Build breadcrumbs from API response
                    // The API breadcrumb contains parent folders only (not the current folder)
                    // Filter out current folder from breadcrumb in case API includes it
                    const parentBreadcrumbs = folderDetail.breadcrumb.filter(f => f.folder_id !== folderDetail.folder_id);
                    const currentFolderName = folderDetail.name || folderDetail.container_number || `Folder`;
                    setBreadcrumbs([
                        { id: "root", name: t("drive.breadcrumb.home"), type: "root" as const },
                        ...parentBreadcrumbs.map(f => ({
                            id: f.folder_id,
                            name: f.name || f.container_number || `Folder`,
                            type: (f.folder_type === "container" ? "container" : "custom_folder") as "container" | "custom_folder",
                        })),
                        // Add current folder to breadcrumb
                        {
                            id: folderDetail.folder_id,
                            name: currentFolderName,
                            type: "custom_folder" as const,
                        },
                    ]);
                } else if (folderDetail.folder_type === "container") {
                    // Container folder: show documents
                    setContainers([]);
                    setCustomFolders([]);

                    // Convert API documents to DocumentFile format
                    const docs: DocumentFile[] = folderDocuments?.map(doc => ({
                        id: doc.document_id,
                        containerId: folderDetail.container_id || "",
                        type: (doc.document_type as any) || "unknown",
                        typeName: doc.document_type ? docTypeNameMap.get(doc.document_type) : undefined,
                        name: doc.file_name || "",
                        uploadedAt: new Date(doc.upload_timestamp),
                        updatedAt: doc.updated_at ? new Date(doc.updated_at) : undefined,
                        uploadSource: doc.upload_source || undefined,
                        fileUrl: `/documents/${doc.document_id}/download`, // Will need to use download endpoint
                        fileSize: 0, // Not available in API response
                        mimeType: doc.file_mime_type || "",
                        extractedData: {},
                        missingFields: [],
                        status: doc.extraction_status === "extracted" ? "Completo" : "Pending",
                        extractionStatus: (doc.extraction_status as any) || undefined,
                        validationStatus: (doc.validation_status as DocumentFile["validationStatus"]) ?? undefined,
                    })) || [];
                    setDocuments(docs);

                    // Build breadcrumbs from API response
                    // The API breadcrumb contains parent folders only (not the current folder)
                    // Filter out current folder from breadcrumb in case API includes it
                    const containerParentBreadcrumbs = folderDetail.breadcrumb.filter(f => f.folder_id !== folderDetail.folder_id);
                    // Use same naming logic as container list: container_number or Container + container_id prefix
                    const containerFolderName = folderDetail.container_number || folderDetail.name || (folderDetail.container_id ? `Container ${folderDetail.container_id.slice(0, 8).toUpperCase()}` : `Container`);
                    setBreadcrumbs([
                        { id: "root", name: t("drive.breadcrumb.home"), type: "root" as const },
                        ...containerParentBreadcrumbs.map(f => ({
                            id: f.folder_id,
                            name: f.name || f.container_number || (f.container_id ? `Container ${f.container_id.slice(0, 8).toUpperCase()}` : `Folder`),
                            type: (f.folder_type === "container" ? "container" : "custom_folder") as "container" | "custom_folder",
                            containerId: f.folder_type === "container" ? (f.container_id || undefined) : undefined,
                        })),
                        // Add current folder to breadcrumb
                        {
                            id: folderDetail.folder_id,
                            name: containerFolderName,
                            type: "container" as const,
                            containerId: folderDetail.container_id || undefined,
                        },
                    ]);
                }
            } else if (!currentFolderId) {
                // Root level: show containers and custom folders
                // Use allFoldersData to get both container and custom folders at root
                // Only show container folders that are at root (parent_id is null) — moved ones appear under their parent
                const containerFolders = allFoldersData?.filter(f => f.folder_type === "container" && f.parent_id === null) || [];

                // Create Container objects from API folder data
                // Use folder_id as the id since that's what we navigate to when clicking
                const shipperContainers: Container[] = containerFolders
                    .filter(f => f.container_id !== null)
                    .map(f => {
                        // Create container from API folder data
                        // IMPORTANT: Use folder_id as id for navigation (not container_id)
                        return {
                            id: f.folder_id,  // Use folder_id for navigation
                            shipperId: "",
                            receiverId: "",
                            containerNumber: f.container_number || `Container ${f.container_id!.slice(0, 8)}`,
                            route: { from: "", to: "" },
                            progress: 0,
                            eta: new Date(),
                            documentStatus: f.document_count > 0 ? "Completo" : "Pending",
                            riskLevel: "Low" as const,
                            value: { amount: 0, currency: "EUR" },
                            status: "In Transit" as const,
                            commodity: "avocado" as const,
                            createdAt: new Date(f.created_at),
                            updatedAt: new Date(f.updated_at),
                        };
                    });
                setContainers(shipperContainers);

                // Get root custom folders (parent_id is null)
                const rootFolders = allFoldersData?.filter(f => f.folder_type === "custom" && f.parent_id === null) || [];
                setCustomFolders(rootFolders.map(f => ({
                    id: f.folder_id,
                    name: f.name || "",
                    parentId: f.parent_id,
                    containerIds: [],
                    createdAt: new Date(f.created_at),
                    updatedAt: new Date(f.updated_at),
                })));

                setDocuments([]);
                setBreadcrumbs([{ id: "root", name: t("drive.breadcrumb.home"), type: "root" }]);
            }
        } else {
            // Unmapped files tab
            const unmapped: UnmappedFile[] = unmappedDocumentsData?.map(doc => ({
                id: doc.document_id,
                name: doc.file_name || "",
                type: (doc.document_type as any) || "unknown",
                        typeName: doc.document_type ? docTypeNameMap.get(doc.document_type) : undefined,
                uploadedAt: new Date(doc.upload_timestamp),
                fileUrl: `/documents/${doc.document_id}/download`,
                fileSize: 0,
                mimeType: doc.file_mime_type || "",
                extractedData: {},
                missingFields: [],
                status: doc.extraction_status === "extracted" ? "Completo" : "Pending",
            })) || [];
            setUnmappedFiles(unmapped);
            setContainers([]);
            setCustomFolders([]);
            setDocuments([]);
            setBreadcrumbs([]);
        }
    }, [activeTab, currentFolderId, foldersData, allFoldersData, folderDetail, folderDocuments, unmappedDocumentsData, t]);

    // Track if we've already handled the containerId param this session
    const handledContainerIdRef = useRef<string | null>(null);

    // Handle containerId URL param - find and navigate to the container's folder
    // This should override any stored preferences when explicitly navigating with containerId
    useEffect(() => {
        // Only process if we have a containerId param and it hasn't been handled yet
        if (urlContainerId && allFoldersData && handledContainerIdRef.current !== urlContainerId) {
            // Find the folder for this container_id
            const containerFolder = allFoldersData.find(
                f => f.folder_type === "container" && f.container_id === urlContainerId
            );
            if (containerFolder) {
                // Mark as handled so we don't re-process on every render
                handledContainerIdRef.current = urlContainerId;
                // Navigate to this container folder, overriding any stored preference
                setCurrentFolderId(containerFolder.folder_id);
                setSearchParams({ tab: "myDrive", folder: containerFolder.folder_id, view: viewMode });
            }
        }
    }, [urlContainerId, allFoldersData, setSearchParams, viewMode]);

    // Debounce search query for API calls
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
            if (searchQuery.trim()) {
                setIsSearching(true);
            } else {
                setIsSearching(false);
                setSearchResults([]);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Update search results when API data changes
    useEffect(() => {
        if (!debouncedSearchQuery) {
            setSearchResults([]);
            setSearchSectionFolders({ containers: [], customFolders: [] });
            setSearchSectionDocuments([]);
            setSearchSectionContainers([]);
            setIsSearching(false);
            return;
        }

        if (searchData) {
            const results: Array<{
                type: "document" | "unmapped_file" | "container" | "custom_folder";
                item: DocumentFile | UnmappedFile | Container | CustomFolder;
            }> = [];
            const sectionFoldersContainers: Container[] = [];
            const sectionFoldersCustom: CustomFolder[] = [];
            const sectionDocuments: DocumentFile[] = [];
            const sectionContainers: Container[] = [];

            // Map folder results (for "Cartelle" section)
            searchData.folders.forEach(folder => {
                if (folder.folder_type === "container") {
                    const item: Container = {
                        id: folder.id,
                        shipperId: "",
                        receiverId: "",
                        containerNumber: folder.container_number || folder.name,
                        route: { from: "", to: "" },
                        progress: 0,
                        eta: new Date(),
                        documentStatus: "Pending",
                        riskLevel: "Low",
                        value: { amount: 0, currency: "USD" },
                        status: "In Transit",
                        commodity: "avocado",
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    };
                    results.push({ type: "container", item });
                    sectionFoldersContainers.push(item);
                } else {
                    const item: CustomFolder = {
                        id: folder.id,
                        name: folder.name,
                        parentId: folder.parent_id || null,
                        containerIds: [],
                        createdAt: new Date(),
                        updatedAt: new Date(),
                    };
                    results.push({ type: "custom_folder", item });
                    sectionFoldersCustom.push(item);
                }
            });

            // Map document results (for "Documenti" section)
            searchData.documents.forEach(doc => {
                const item: DocumentFile = {
                    id: doc.id,
                    containerId: doc.container_id || null,
                    type: (doc.document_type as DocumentFile["type"]) || "unknown",
                    typeName: doc.document_type ? docTypeNameMap.get(doc.document_type) : undefined,
                    name: doc.name,
                    uploadedAt: new Date(),
                    extractedData: {},
                    missingFields: [],
                    status: "Pending",
                    fileUrl: "",
                    fileSize: 0,
                    mimeType: doc.file_mime_type || "application/pdf",
                } as DocumentFile;
                results.push({ type: "document", item });
                sectionDocuments.push(item);
            });

            // Map container results (for "Container" section; from API containers array)
            searchData.containers.forEach(container => {
                const matchingFolder = searchData.folders.find(
                    f => f.folder_type === "container" && f.container_id === container.id
                );
                const folderId = matchingFolder?.id;
                const containerItem: Container = {
                    id: folderId ?? container.id,
                    shipperId: "",
                    receiverId: "",
                    containerNumber: container.container_number || container.name,
                    route: { from: "", to: "" },
                    progress: 0,
                    eta: new Date(),
                    documentStatus: "Pending",
                    riskLevel: "Low",
                    value: { amount: 0, currency: "USD" },
                    status: "In Transit",
                    commodity: "avocado",
                    createdAt: new Date(),
                    updatedAt: new Date(),
                };
                if (folderId) {
                    const alreadyAdded = results.some(
                        r => r.type === "container" && (
                            (r.item as Container).containerNumber === (container.container_number || container.name) ||
                            r.item.id === matchingFolder.id
                        )
                    );
                    if (!alreadyAdded) results.push({ type: "container", item: containerItem });
                }
                sectionContainers.push(containerItem);
            });

            setSearchResults(results);
            setSearchSectionFolders({ containers: sectionFoldersContainers, customFolders: sectionFoldersCustom });
            setSearchSectionDocuments(sectionDocuments);
            setSearchSectionContainers(sectionContainers);
            // Do NOT set isSearching(false) here: keep search mode until user clears query or clicks tab
        }
    }, [searchData, debouncedSearchQuery]);

    // Update isSearching based on loading state
    useEffect(() => {
        if (isSearchLoading && debouncedSearchQuery) {
            setIsSearching(true);
        }
    }, [isSearchLoading, debouncedSearchQuery]);

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && searchQuery.trim()) {
            setDebouncedSearchQuery(searchQuery.trim());
            setIsSearching(true);
        }
    };

    // Helper to clear search state
    const clearSearchState = () => {
        setIsSearching(false);
        setSearchQuery("");
        setDebouncedSearchQuery("");
        setSearchResults([]);
    };

    // Navigation handlers
    const handleContainerClick = (container: Container) => {
        // Clear search state when navigating to folder
        clearSearchState();
        setCurrentFolderId(container.id);
        setSearchParams({ tab: "myDrive", folder: container.id, view: viewMode });
        setSelectedFolderIds(new Set());
        setSelectedDocumentIds(new Set());
    };

    const handleCustomFolderClick = (folder: CustomFolder) => {
        // Clear search state when navigating to folder
        clearSearchState();
        setCurrentFolderId(folder.id);
        setSearchParams({ tab: "myDrive", folder: folder.id, view: viewMode });
        setSelectedFolderIds(new Set());
        setSelectedDocumentIds(new Set());
    };

    const handleDocumentClick = (document: DocumentFile) => {
        // If document has a container, navigate to the unified viewer
        // Otherwise, navigate to the standalone document manager
        if (document.containerId) {
            navigate(`/shipper/containers/${document.containerId}/documents/${document.id}?mode=manager`, { state: navigationStateFrom(location) });
        } else {
            navigate(`/shipper/documents/${document.id}`, { state: navigationStateFrom(location) });
        }
    };

    const handleBreadcrumbClick = (item: BreadcrumbItem) => {
        if (item.type === "root") {
            setCurrentFolderId(null);
            setSearchParams({ tab: "myDrive", view: viewMode });
        } else {
            setCurrentFolderId(item.id);
            setSearchParams({ tab: "myDrive", folder: item.id, view: viewMode });
        }
        setSelectedFolderIds(new Set());
        setSelectedDocumentIds(new Set());
    };

    // Context Menu handlers
    const handleDocumentContextMenu = (document: DocumentFile, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Build extraction actions based on document status
        const extractionActions: ContextMenuItem[] = [];
        const status = document.extractionStatus;

        // Helper function to handle extraction with feedback
        const handleExtraction = (strategy: "vision" | "olmo_ocr_claude", documentName: string) => {
            toast.info(t("drive.document.extraction.started", {
                defaultValue: `Starting extraction with ${strategy === "vision" ? "Vision" : "OlmoOCR"} strategy...`
            }));
            extractDocumentMutation.mutate({
                documentId: document.id,
                request: { strategy, force_reextract: true }
            }, {
                onSuccess: () => {
                    toast.success(t("drive.document.extraction.success", {
                        defaultValue: `Extraction started for "${documentName}". This may take a few moments.`
                    }));
                    // Invalidate queries to refresh document status
                    if (currentFolderId) {
                        queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(currentFolderId) });
                        queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(currentFolderId) });
                    }
                },
                onError: (error: any) => {
                    toast.error(t("drive.document.extraction.error", {
                        defaultValue: `Extraction failed: ${error?.message || "Unknown error"}`
                    }));
                }
            });
        };

        // For pending or failed documents - show "Parse with..." options
        if (!status || status === "pending" || status === "failed") {
            extractionActions.push(
                {
                    id: "extract-vision",
                    label: t("drive.document.actions.parseVision", { defaultValue: "Parse with Vision" }),
                    icon: Zap,
                    onAction: () => handleExtraction("vision", document.name),
                },
                {
                    id: "extract-olmo",
                    label: t("drive.document.actions.parseOlmo", { defaultValue: "Parse with OlmoOCR" }),
                    icon: Zap,
                    onAction: () => handleExtraction("olmo_ocr_claude", document.name),
                }
            );
        }

        // For extracted/completed documents - show "Re-parse with..." options
        if (status === "extracted" || status === "completed" || status === "partially_completed") {
            extractionActions.push(
                {
                    id: "reextract-vision",
                    label: t("drive.document.actions.reparseVision", { defaultValue: "Re-parse with Vision" }),
                    icon: RefreshCw01,
                    onAction: () => handleExtraction("vision", document.name),
                },
                {
                    id: "reextract-olmo",
                    label: t("drive.document.actions.reparseOlmo", { defaultValue: "Re-parse with OlmoOCR" }),
                    icon: RefreshCw01,
                    onAction: () => handleExtraction("olmo_ocr_claude", document.name),
                }
            );
        }

        const items: ContextMenuItem[] = [
            {
                id: "download",
                label: t("drive.document.actions.download"),
                icon: Download01,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowDownloadModal(true);
                },
            },
            {
                id: "create-version",
                label: t("drive.document.actions.createVersion", { defaultValue: "Create new version" }),
                icon: Clock,
                onAction: () => {
                    setSelectedDocument(document);
                    setVersionFile(null);
                    setShowCreateVersionModal(true);
                },
            },
            ...extractionActions,
            {
                id: "classify",
                label: t("drive.document.actions.classify", { defaultValue: "Classify Document" }),
                icon: Tag01,
                onAction: () => {
                    setSelectedDocument(document);
                    setSelectedDocumentType(document.type || "");
                    setShowClassifyModal(true);
                },
            },
            {
                id: "rename",
                label: t("drive.document.actions.rename"),
                icon: Edit01,
                onAction: () => {
                    setSelectedDocument(document);
                    setRenameValue(document.name);
                    setShowRenameModal(true);
                },
            },
            {
                id: "move",
                label: t("drive.document.actions.move"),
                icon: Move,
                onAction: () => {
                    setSelectedDocument(document);
                    setSelectedContainerId(document.containerId || "");
                    setShowMoveModal(true);
                },
            },
            {
                id: "share",
                label: t("drive.document.actions.share"),
                icon: Share01,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowShareModal(true);
                },
            },
            {
                id: "delete",
                label: t("drive.document.actions.delete"),
                icon: Trash01,
                isDestructive: true,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowDeleteModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const handleContainerContextMenu = (container: Container, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "open",
                label: t("drive.folder.open"),
                icon: Folder,
                onAction: () => {
                    handleContainerClick(container);
                },
            },
            {
                id: "move",
                label: t("drive.folder.moveAction"),
                icon: Move,
                onAction: () => {
                    setSelectedContainer(container);
                    setSelectedParentFolderId(null);
                    setShowMoveFolderModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const handleCustomFolderContextMenu = (folder: CustomFolder, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "open",
                label: t("drive.folder.open"),
                icon: Folder,
                onAction: () => {
                    handleCustomFolderClick(folder);
                },
            },
            {
                id: "rename",
                label: t("drive.folder.rename"),
                icon: Edit01,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setRenameValue(folder.name);
                    setShowRenameModal(true);
                },
            },
            {
                id: "move",
                label: t("drive.folder.moveAction"),
                icon: Move,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setSelectedParentFolderId(folder.parentId);
                    setShowMoveFolderModal(true);
                },
            },
            {
                id: "delete",
                label: t("drive.folder.delete"),
                icon: Trash01,
                isDestructive: true,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setShowDeleteModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const closeContextMenu = () => {
        setContextMenu({
            isOpen: false,
            position: null,
            items: [],
        });
    };

    // Generic context menu handler (for empty area)
    const handleGenericContextMenu = (e: React.MouseEvent) => {
        // Don't show context menu during search
        if (isSearching) return;

        // Don't show context menu for container folders (subfolder creation forbidden)
        if (folderDetail?.folder_type === "container") return;

        // Only show if clicking on empty area (not on a file/folder)
        const target = e.target as HTMLElement;
        if (target.closest('[data-document-item]') || target.closest('[data-folder-item]')) {
            return; // Don't show generic menu if clicking on item
        }

        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "createFolder",
                label: t("drive.folder.create"),
                icon: Folder,
                onAction: () => {
                    setNewFolderName("");
                    setSelectedParentFolderId(currentFolderId);
                    setShowCreateFolderModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    // Modal handlers
    const handleRename = () => {
        if (selectedDocument && renameValue.trim()) {
            // TODO: Implement document rename API
            setShowRenameModal(false);
            setSelectedDocument(null);
            setRenameValue("");
        } else if (selectedCustomFolder && renameValue.trim()) {
            updateFolderMutation.mutate(
                {
                    folderId: selectedCustomFolder.id,
                    data: { name: renameValue.trim() },
                },
                {
                    onSuccess: () => {
                        setShowRenameModal(false);
                        setSelectedCustomFolder(null);
                        setRenameValue("");
                    },
                }
            );
        }
    };

    // Bulk selection handlers
    const handleDocumentSelect = (document: DocumentFile, checked: boolean) => {
        const newSelected = new Set(selectedDocumentIds);
        if (checked) {
            newSelected.add(document.id);
        } else {
            newSelected.delete(document.id);
        }
        setSelectedDocumentIds(newSelected);
    };

    const handleSelectAllDocuments = () => {
        if (selectedDocumentIds.size === documents.length && documents.length > 0) {
            setSelectedDocumentIds(new Set());
        } else {
            setSelectedDocumentIds(new Set(documents.map(d => d.id)));
        }
    };

    // Folder selection handlers (works for both custom folders and container folders)
    const handleFolderSelect = (folder: CustomFolder, checked: boolean) => {
        const newSelected = new Set(selectedFolderIds);
        if (checked) {
            newSelected.add(folder.id);
        } else {
            newSelected.delete(folder.id);
        }
        setSelectedFolderIds(newSelected);
    };

    const handleContainerFolderSelect = (container: Container, checked: boolean) => {
        const newSelected = new Set(selectedFolderIds);
        if (checked) {
            newSelected.add(container.id); // container.id is already folder_id
        } else {
            newSelected.delete(container.id);
        }
        setSelectedFolderIds(newSelected);
    };

    const handleSelectAllFolders = () => {
        const allFolderItems = [...customFolders.map(f => f.id), ...containers.map(c => c.id)];
        if (selectedFolderIds.size === allFolderItems.length && allFolderItems.length > 0) {
            setSelectedFolderIds(new Set());
        } else {
            setSelectedFolderIds(new Set(allFolderItems));
        }
    };

    const handleBulkMoveFolders = () => {
        if (selectedFolderIds.size === 0) return;
        setShowMoveFolderModal(true);
    };

    const handleBulkDeleteFolders = () => {
        if (selectedFolderIds.size === 0) return;
        if (selectedFoldersTotalDocCount > 0) {
            // Has documents — show warning modal
            setShowDeleteFoldersModal(true);
        } else {
            // All empty — delete directly
            executeBulkDeleteFolders();
        }
    };

    const executeBulkDeleteFolders = () => {
        setIsDeletingFolders(true);
        const folderIds = Array.from(selectedFolderIds);
        let completed = 0;
        let failed = 0;

        folderIds.forEach(folderId => {
            deleteFolderMutation.mutate(folderId, {
                onSuccess: () => {
                    completed++;
                    if (completed + failed === folderIds.length) {
                        if (completed > 0) toast.success(`${completed} folder${completed > 1 ? 's' : ''} deleted`);
                        if (failed > 0) toast.error(`${failed} folder${failed > 1 ? 's' : ''} failed to delete`);
                        setShowDeleteFoldersModal(false);
                        setSelectedFolderIds(new Set());
                        setIsDeletingFolders(false);
                    }
                },
                onError: () => {
                    failed++;
                    if (completed + failed === folderIds.length) {
                        if (completed > 0) toast.success(`${completed} folder${completed > 1 ? 's' : ''} deleted`);
                        if (failed > 0) toast.error(`${failed} folder${failed > 1 ? 's' : ''} failed to delete`);
                        setShowDeleteFoldersModal(false);
                        setSelectedFolderIds(new Set());
                        setIsDeletingFolders(false);
                    }
                },
            });
        });
    };

    // Note: bulk move logic is now inside executeMoveFolder()

    const handleBulkDelete = () => {
        if (selectedDocumentIds.size === 0) return;
        setShowDeleteModal(true);
    };

    const handleDelete = () => {
        if (selectedDocumentIds.size > 0) {
            // Bulk delete
            const documentIds = Array.from(selectedDocumentIds);
            if (currentFolderId) {
                // Remove from folder (becomes unmapped)
                Promise.all(
                    documentIds.map(docId =>
                        removeDocumentFromFolderMutation.mutateAsync({ folderId: currentFolderId, documentId: docId })
                    )
                ).then(() => {
                    setShowDeleteModal(false);
                    setSelectedDocumentIds(new Set());
                    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(currentFolderId) });
                    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(currentFolderId) });
                });
            } else {
                // Delete documents completely
                Promise.all(
                    documentIds.map(docId => deleteDocumentMutation.mutateAsync(docId))
                ).then(() => {
                    setShowDeleteModal(false);
                    setSelectedDocumentIds(new Set());
                    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.lists() });
                });
            }
        } else if (selectedDocument) {
            if (currentFolderId) {
                // Remove from folder (becomes unmapped)
                removeDocumentFromFolderMutation.mutate(
                    { folderId: currentFolderId, documentId: selectedDocument.id },
                    {
                        onSuccess: () => {
                            setShowDeleteModal(false);
                            setSelectedDocument(null);
                        },
                    }
                );
            } else {
                // Delete document completely
                deleteDocumentMutation.mutate(selectedDocument.id, {
                    onSuccess: () => {
                        setShowDeleteModal(false);
                        setSelectedDocument(null);
                        navigate("/shipper/documents");
                    },
                });
            }
        } else if (selectedCustomFolder) {
            deleteFolderMutation.mutate(selectedCustomFolder.id, {
                onSuccess: () => {
                    setShowDeleteModal(false);
                    setSelectedCustomFolder(null);
                    // Se stiamo navigando nella cartella eliminata, torna alla root
                    if (currentFolderId === selectedCustomFolder.id) {
                        setCurrentFolderId(null);
                        setSearchParams({ tab: "myDrive", view: viewMode });
                    }
                },
            });
        }
    };

    const handleMove = () => {
        if (!selectedContainerId) return;

        // Collect document IDs to associate
        const docIds: string[] = selectedDocument
            ? [selectedDocument.id]
            : Array.from(selectedDocumentIds);

        if (docIds.length === 0) return;

        // Update each document's container_id via the real API
        let completed = 0;
        let failed = 0;
        docIds.forEach(docId => {
            updateDocumentMutation.mutate(
                { documentId: docId, data: { container_id: selectedContainerId } },
                {
                    onSuccess: () => {
                        completed++;
                        if (completed + failed === docIds.length) {
                            const containerLabel = containerSelectItems.find(c => c.id === selectedContainerId)?.label || selectedContainerId;
                            toast.success(`${completed} document${completed > 1 ? 's' : ''} associated to ${containerLabel}`);
                            setShowMoveModal(false);
                            setSelectedDocument(null);
                            setSelectedDocumentIds(new Set());
                            setSelectedContainerId("");
                            queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.all });
                        }
                    },
                    onError: () => {
                        failed++;
                        if (completed + failed === docIds.length) {
                            toast.error(`${failed} document${failed > 1 ? 's' : ''} failed to associate`);
                            setShowMoveModal(false);
                            setSelectedDocument(null);
                            setSelectedDocumentIds(new Set());
                            setSelectedContainerId("");
                        }
                    },
                }
            );
        });
    };

    const handleShare = () => {
        if (selectedDocument) {
            // TODO: Implement document share API
            setShowShareModal(false);
            setSelectedDocument(null);
        }
    };

    const handleDownload = () => {
        if (selectedDocument) {
            // Simula download
            const link = window.document.createElement("a");
            link.href = selectedDocument.fileUrl;
            link.download = selectedDocument.name;
            link.click();
            setShowDownloadModal(false);
            setSelectedDocument(null);
        }
    };

    // Cleanup + navigate to target folder after move
    const cleanupMoveFolder = (targetFolderId: string | null) => {
        setShowMoveFolderModal(false);
        setSelectedCustomFolder(null);
        setSelectedContainer(null);
        setSelectedParentFolderId(null);
        setSelectedFolderIds(new Set());
        setIsCreatingInlineFolder(false);
        setInlineNewFolderName("");
        setIsMovingFolder(false);

        // Navigate to the target folder
        if (targetFolderId) {
            setCurrentFolderId(targetFolderId);
            setSearchParams({ tab: "myDrive", folder: targetFolderId, view: viewMode });
        } else {
            // Moved to root
            setCurrentFolderId(null);
            setSearchParams({ tab: "myDrive", view: viewMode });
        }
    };

    // Core move logic — takes the resolved target parent ID
    const executeMoveFolder = (targetParentId: string | null) => {
        // Bulk folder move
        if (selectedFolderIds.size > 0 && !selectedCustomFolder && !selectedContainer) {
            const folderIds = Array.from(selectedFolderIds);
            let completed = 0;
            let failed = 0;
            folderIds.forEach(folderId => {
                moveFolderMutation.mutate(
                    { folderId, data: { new_parent_id: targetParentId } },
                    {
                        onSuccess: () => {
                            completed++;
                            if (completed + failed === folderIds.length) {
                                toast.success(`${completed} folder${completed > 1 ? 's' : ''} moved`);
                                cleanupMoveFolder(targetParentId);
                            }
                        },
                        onError: () => {
                            failed++;
                            if (completed + failed === folderIds.length) {
                                toast.error(`${failed} folder${failed > 1 ? 's' : ''} failed to move`);
                                cleanupMoveFolder(targetParentId);
                            }
                        },
                    }
                );
            });
            return;
        }

        const folderId = selectedCustomFolder?.id ?? selectedContainer?.id;
        if (!folderId) {
            setIsMovingFolder(false);
            return;
        }

        moveFolderMutation.mutate(
            { folderId, data: { new_parent_id: targetParentId } },
            {
                onSuccess: () => cleanupMoveFolder(targetParentId),
                onError: () => {
                    toast.error("Failed to move folder");
                    setIsMovingFolder(false);
                },
            }
        );
    };

    const handleMoveFolder = () => {
        // Guard against double-clicks
        if (isMovingFolder) return;
        setIsMovingFolder(true);

        // If creating a new folder inline with a name, create it first then move into it
        if (isCreatingInlineFolder && inlineNewFolderName.trim()) {
            createFolderMutation.mutate(
                { name: inlineNewFolderName.trim(), parent_id: currentFolderId },
                {
                    onSuccess: (newFolder) => {
                        if (newFolder?.folder_id) {
                            executeMoveFolder(newFolder.folder_id);
                        } else {
                            setIsMovingFolder(false);
                        }
                    },
                    onError: () => {
                        setIsMovingFolder(false);
                    },
                }
            );
            return;
        }

        // Fall through to dropdown selection (even if inline input was open but empty)
        executeMoveFolder(selectedParentFolderId);
    };

    const handleCreateFolder = () => {
        if (!newFolderName.trim()) return;
        createFolderMutation.mutate(
            {
                name: newFolderName.trim(),
                parent_id: selectedParentFolderId,
            },
            {
                onSuccess: () => {
                    setShowCreateFolderModal(false);
                    setNewFolderName("");
                    setSelectedParentFolderId(null);
                },
            }
        );
    };

    // Handle file selection/drop
    const handleFilesSelected = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const newFiles: UploadFileItem[] = fileArray.map((file) => ({
            file,
            id: `${Date.now()}-${Math.random()}`,
            customName: file.name,
            progress: 0,
            status: "pending" as const,
        }));

        setSelectedFiles((prev) => [...prev, ...newFiles]);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Don't show drag indicator for container folders (upload forbidden)
        if (folderDetail?.folder_type === "container") return;
        if (!isDraggingOver) {
            setIsDraggingOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set to false if we're leaving the drop zone entirely
        const relatedTarget = e.relatedTarget as Node | null;
        if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
            setIsDraggingOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);

        // Allow drop only if we're inside a custom folder (NOT container folders - upload forbidden)
        // For root, we don't allow direct upload (user should create a folder first)
        if (!currentFolderId) return;
        if (currentFolderId && !folderDetail) return; // Wait for folder detail to load
        if (folderDetail?.folder_type === "container") return; // Upload forbidden for container folders

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFilesSelected(files);
        }
    };

    const handleAddMoreFiles = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFilesSelected(e.target.files);
        }
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (fileId: string) => {
        setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
    };

    // Helper function for XHR upload with progress tracking
    const uploadFileWithProgress = async (
        fileItem: UploadFileItem,
        containerId?: string | null
    ): Promise<string> => {
        const formData = new FormData();
        formData.append("file", fileItem.file);

        if (containerId) {
            formData.append("container_id", containerId);
        }
        if (fileItem.customName && fileItem.customName !== fileItem.file.name) {
            formData.append("file_name", fileItem.customName);
        }
        if (fileItem.documentType) {
            formData.append("document_type", fileItem.documentType);
        }

        const result = await new Promise<{ document_id: string }>((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (e) => {
                if (e.lengthComputable) {
                    const progress = Math.round((e.loaded / e.total) * 100);
                    setSelectedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileItem.id ? { ...f, progress } : f
                        )
                    );
                }
            });

            xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        resolve(response);
                    } catch (e) {
                        reject(new Error("Invalid response format"));
                    }
                } else {
                    reject(new Error(`Upload failed: ${xhr.statusText}`));
                }
            });

            xhr.addEventListener("error", () => {
                reject(new Error("Network error during upload"));
            });

            xhr.addEventListener("abort", () => {
                reject(new Error("Upload aborted"));
            });

            // Get API base URL
            const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
            const cleanBaseUrl = baseUrl.replace(/\/$/, "");
            const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
            const apiBaseUrl = `${cleanBaseUrl}${cleanVersionPrefix}`;

            xhr.open("POST", `${apiBaseUrl}/documents/upload`);

            // Add auth token if available
            const token = storage.getItem("access_token");
            if (token) {
                xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            }

            xhr.send(formData);
        });

        return result.document_id;
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setIsUploading(true);

        const pendingFiles = selectedFiles.filter((f) => f.status === "pending");
        const uploadedDocumentIds: string[] = [];

        try {
            for (const fileItem of pendingFiles) {
                // Update status to uploading
                setSelectedFiles((prev) =>
                    prev.map((f) =>
                        f.id === fileItem.id
                            ? { ...f, status: "uploading" as const, progress: 0 }
                            : f
                    )
                );

                try {
                    let documentId: string;

                    if (folderDetail?.folder_type === "container" && folderDetail.container_id) {
                        // Upload to container folder
                        documentId = await uploadFileWithProgress(fileItem, folderDetail.container_id);
                    } else {
                        // Upload as unmapped (no container_id) - for custom folders, root level, or no folder
                        documentId = await uploadFileWithProgress(fileItem, null);
                    }

                    // Update to completed
                    setSelectedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileItem.id
                                ? {
                                    ...f,
                                    status: "completed" as const,
                                    progress: 100,
                                    documentId,
                                }
                                : f
                        )
                    );

                    uploadedDocumentIds.push(documentId);
                } catch (error: any) {
                    // Update to error
                    setSelectedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileItem.id
                                ? {
                                    ...f,
                                    status: "error" as const,
                                    error: error?.message || t("drive.upload.error.generic"),
                                }
                                : f
                        )
                    );
                }
            }

            // Associate documents to custom folder if needed
            if (
                folderDetail?.folder_type === "custom" &&
                currentFolderId &&
                uploadedDocumentIds.length > 0
            ) {
                try {
                    await associateDocumentsMutation.mutateAsync({
                        folderId: currentFolderId,
                        data: { document_ids: uploadedDocumentIds },
                    });
                } catch (error) {
                    console.error("Failed to associate documents to folder:", error);
                }
            }

            // Invalidate queries to refresh folder content after successful uploads
            if (uploadedDocumentIds.length > 0) {
                if (currentFolderId) {
                    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(currentFolderId) });
                    queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(currentFolderId) });
                }
                queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.unmapped() });
                queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.list() });
            }

            // Reset after successful uploads if all completed
            const allCompleted = selectedFiles.every(
                (f) => f.status === "completed" || f.status === "error"
            );
            if (allCompleted && uploadedDocumentIds.length > 0) {
                // Wait a bit to show completion state, then clear
                setTimeout(() => {
                    setSelectedFiles([]);
                }, 2000);
            }
        } finally {
            setIsUploading(false);
        }
    };

    const getAvailableFoldersForMove = (): Array<{ id: string | null; label: string }> => {
        const allFolders = foldersData?.filter(f => f.folder_type === "custom") || [];
        const currentFolderId = selectedCustomFolder?.id;

        // Exclude the current folder, selected bulk folders, and self-references
        const excludeIds = new Set<string>();
        if (currentFolderId) excludeIds.add(currentFolderId);
        selectedFolderIds.forEach(id => excludeIds.add(id));

        const availableFolders = allFolders.filter(f => {
            if (excludeIds.has(f.folder_id)) return false;
            if (folderDetail && folderDetail.folder_id === f.folder_id) {
                return false;
            }
            return true;
        });

        const options: Array<{ id: string | null; label: string }> = [
            { id: null, label: t("drive.folder.move.root") },
            ...availableFolders.map(f => ({ id: f.folder_id, label: f.name || "" })),
        ];

        return options;
    };

    // Update URL when tab/view changes
    useEffect(() => {
        const params = new URLSearchParams();
        params.set("tab", activeTab);
        params.set("view", viewMode);
        if (currentFolderId) {
            params.set("folder", currentFolderId);
        }
        setSearchParams(params, { replace: true });
    }, [activeTab, viewMode, currentFolderId, setSearchParams]);

    // Sync state changes to folder preferences (localStorage persistence)
    useEffect(() => {
        folderPrefs.setViewMode(viewMode);
    }, [viewMode, folderPrefs.setViewMode]);

    useEffect(() => {
        folderPrefs.setLastTab(activeTab);
    }, [activeTab, folderPrefs.setLastTab]);

    useEffect(() => {
        folderPrefs.setLastFolderId(currentFolderId);
    }, [currentFolderId, folderPrefs.setLastFolderId]);

    useEffect(() => {
        folderPrefs.setShowEmptyFolders(showEmptyFolders);
    }, [showEmptyFolders, folderPrefs.setShowEmptyFolders]);

    // Filter empty folders if needed
    const filteredContainers = showEmptyFolders
        ? containers
        : containers.filter(c => {
            // Check if container has documents by looking for its folder in allFoldersData
            // Note: c.id is now folder_id (not container_id)
            const containerFolder = allFoldersData?.find(f => f.folder_id === c.id);
            return (containerFolder?.document_count || 0) > 0;
        });

    // Custom folders are always shown (showEmptyFolders only applies to container folders)
    const filteredCustomFolders = customFolders;

    // Create memoized maps for document counts, updated_at, and created_at
    // Key is folder_id since Container.id is now folder_id
    const { containerDocumentCounts, customFolderDocumentCounts, containerUpdatedAt, customFolderUpdatedAt, containerCreatedAt, customFolderCreatedAt } = useMemo(() => {
        const containerCounts = new Map<string, number>();
        const customFolderCounts = new Map<string, number>();
        const containerDates = new Map<string, Date | string>();
        const customFolderDates = new Map<string, Date | string>();
        const containerCreated = new Map<string, Date | string>();
        const customFolderCreated = new Map<string, Date | string>();

        if (allFoldersData) {
            allFoldersData.forEach(folder => {
                if (folder.folder_type === "container") {
                    // Use folder_id as key since Container.id is folder_id
                    containerCounts.set(folder.folder_id, folder.document_count || 0);
                    containerDates.set(folder.folder_id, folder.updated_at);
                    containerCreated.set(folder.folder_id, folder.created_at);
                } else if (folder.folder_type === "custom") {
                    customFolderCounts.set(folder.folder_id, folder.document_count || 0);
                    customFolderDates.set(folder.folder_id, folder.updated_at);
                    customFolderCreated.set(folder.folder_id, folder.created_at);
                }
            });
        }

        return {
            containerDocumentCounts: containerCounts,
            customFolderDocumentCounts: customFolderCounts,
            containerUpdatedAt: containerDates,
            customFolderUpdatedAt: customFolderDates,
            containerCreatedAt: containerCreated,
            customFolderCreatedAt: customFolderCreated
        };
    }, [allFoldersData]);

    // Check if all selected folders are custom (no container folders) — only custom folders can be deleted
    const allSelectedAreCustomFolders = useMemo(() => {
        if (selectedFolderIds.size === 0) return false;
        const containerIds = new Set(containers.map(c => c.id));
        return Array.from(selectedFolderIds).every(id => !containerIds.has(id));
    }, [selectedFolderIds, containers]);

    // Count total documents in selected folders
    const selectedFoldersTotalDocCount = useMemo(() => {
        let total = 0;
        for (const id of selectedFolderIds) {
            total += customFolderDocumentCounts?.get(id) ?? 0;
        }
        return total;
    }, [selectedFolderIds, customFolderDocumentCounts]);

    // Note: Sorting is now handled inside DocumentList component which combines
    // both custom folders and containers into a single sorted list

    const displayItems = isSearching
        ? {
            containers: searchResults
                .filter(r => r.type === "container")
                .map(r => r.item as Container),
            customFolders: searchResults
                .filter(r => r.type === "custom_folder")
                .map(r => r.item as CustomFolder),
            documents: searchResults
                .filter(r => r.type === "document")
                .map(r => r.item as DocumentFile),
            unmappedFiles: searchResults
                .filter(r => r.type === "unmapped_file")
                .map(r => r.item as UnmappedFile),
        }
        : {
            containers: filteredContainers,
            customFolders: filteredCustomFolders,
            documents,
            unmappedFiles: activeTab === "unmapped" ? unmappedFiles : [],
        };

    // Map unmapped files to DocumentFile[] so they work with DocumentList
    const unmappedAsDocuments = useMemo<DocumentFile[]>(() =>
        displayItems.unmappedFiles.map(f => ({
            id: f.id,
            containerId: null,
            type: f.type,
            name: f.name,
            uploadedAt: f.uploadedAt,
            extractedData: f.extractedData || {},
            missingFields: f.missingFields || [],
            status: f.status,
            fileUrl: f.fileUrl,
            fileSize: f.fileSize,
            mimeType: f.mimeType,
        })),
        [displayItems.unmappedFiles]
    );

    const showSearchLoadingModal = debouncedSearchQuery.length > 0 && isSearchLoading;

    return (
        <div className="space-y-6">
            {/* Search loading modal */}
            {showSearchLoadingModal && (
                <ModalOverlay isOpen={true} isDismissable={false}>
                    <Modal>
                        <Dialog aria-label={t("drive.search.loading")}>
                            <div className="flex flex-col items-center justify-center gap-4 py-8 px-6">
                                <LoadingIndicator type="line-spinner" size="lg" label={t("drive.search.loading")} />
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Header */}
            <div className="flex items-center justify-between gap-6">
                <div>
                    <h1 className="text-2xl font-bold text-primary">{t("drive.title")}</h1>
                </div>
                {/* Search */}
                <div className="relative flex-1">
                    <SearchLg className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 size-5 text-tertiary" />
                    <Input
                        placeholder={t("drive.search.placeholder")}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onKeyDown={handleSearchKeyDown}
                        className="pl-11"
                    />
                </div>
            </div>

            {/* Tabs */}
            <Tabs
                selectedKey={activeTab}
                onSelectionChange={(key) => {
                    setActiveTab(key as "myDrive" | "unmapped");
                    setCurrentFolderId(null);
                    setIsSearching(false);
                    setSearchQuery("");
                    setDebouncedSearchQuery("");
                    setSelectedFolderIds(new Set());
                    setSelectedDocumentIds(new Set());
                }}
            >
                <Tabs.List
                    type="button-border"
                    items={[
                        { id: "myDrive", label: t("drive.myDrive") },
                        { id: "unmapped", label: t("drive.unmappedFiles") },
                    ]}
                >
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* My Drive Tab */}
                <Tabs.Panel id="myDrive">
                    <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <h2 className="text-lg font-semibold text-primary">{t("drive.myDrive")}</h2>
                                {/* Mostra pulsante solo se non siamo dentro una cartella container e non stiamo cercando */}
                                {!isSearching && (!currentFolderId || (folderDetail && folderDetail.folder_type === "custom")) ? (
                                    <Dropdown.Root>
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            iconLeading={<Plus className="size-4" />}
                                        >
                                            {t("drive.add.button")}
                                        </Button>
                                        <Dropdown.Popover>
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    icon={Folder}
                                                    onAction={() => {
                                                        setNewFolderName("");
                                                        setSelectedParentFolderId(currentFolderId);
                                                        setShowCreateFolderModal(true);
                                                    }}
                                                >
                                                    {t("drive.add.folder")}
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    icon={Upload01}
                                                    onAction={() => {
                                                        fileInputRef.current?.click();
                                                    }}
                                                >
                                                    {t("drive.add.files")}
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                ) : null}
                            </div>
                            {/* Breadcrumb */}
                            {breadcrumbs.length > 0 && (
                                <div className="flex items-center gap-2 text-sm">
                                    {breadcrumbs.length <= 2 ? (
                                        // Mostra tutti gli elementi se <= 2 livelli
                                        breadcrumbs.map((item, index) => (
                                            <div key={item.id} className="flex items-center gap-2">
                                                {index > 0 && <ChevronRight className="size-4 text-tertiary" />}
                                                <button
                                                    onClick={() => handleBreadcrumbClick(item)}
                                                    className={`hover:text-primary transition-colors ${index === breadcrumbs.length - 1
                                                        ? "text-primary font-medium"
                                                        : "text-tertiary cursor-pointer"
                                                        }`}
                                                >
                                                    {item.type === "root" ? t("drive.breadcrumb.home") : item.name}
                                                </button>
                                                {/* Go to Container button for container folders */}
                                                {item.type === "container" && item.containerId && (
                                                    <button
                                                        onClick={() => navigate(`/shipper/containers/${item.containerId}`, { state: navigationStateFrom(location) })}
                                                        className="text-tertiary hover:text-primary transition-colors"
                                                        title={t("drive.goToContainer", { defaultValue: "Go to Container" })}
                                                    >
                                                        <Package className="size-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        // Mostra My Drive + ellipsis dropdown + cartella corrente se > 2 livelli
                                        <>
                                            {/* My Drive (primo elemento) */}
                                            <button
                                                onClick={() => handleBreadcrumbClick(breadcrumbs[0])}
                                                className="text-tertiary cursor-pointer hover:text-primary transition-colors"
                                            >
                                                {breadcrumbs[0].type === "root" ? t("drive.breadcrumb.home") : breadcrumbs[0].name}
                                            </button>

                                            {/* Separatore */}
                                            <ChevronRight className="size-4 text-tertiary" />

                                            {/* Dropdown ellipsis con elementi intermedi */}
                                            {breadcrumbs.slice(1, -1).length > 0 && (
                                                <Dropdown.Root>
                                                    <Dropdown.DotsHorizontalButton
                                                        className="text-tertiary hover:text-primary"
                                                        aria-label="Show intermediate folders"
                                                    />
                                                    <Dropdown.Popover placement="bottom start" className="min-w-[200px]">
                                                        <Dropdown.Menu
                                                            onAction={(key) => {
                                                                const item = breadcrumbs.find(b => b.id === key as string);
                                                                if (item) {
                                                                    handleBreadcrumbClick(item);
                                                                }
                                                            }}
                                                        >
                                                            {breadcrumbs.slice(1, -1).map((item) => (
                                                                <Dropdown.Item
                                                                    key={item.id}
                                                                    id={item.id}
                                                                    label={item.name}
                                                                    icon={item.type === "container" ? Package : Folder}
                                                                />
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown.Popover>
                                                </Dropdown.Root>
                                            )}

                                            {/* Separatore */}
                                            <ChevronRight className="size-4 text-tertiary" />

                                            {/* Cartella corrente (ultimo elemento) */}
                                            <button
                                                onClick={() => handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 1])}
                                                className="text-primary font-medium cursor-pointer hover:text-primary transition-colors"
                                            >
                                                {breadcrumbs[breadcrumbs.length - 1].type === "root"
                                                    ? t("drive.breadcrumb.home")
                                                    : breadcrumbs[breadcrumbs.length - 1].name}
                                            </button>
                                            {/* Go to Container button for container folders (last element) */}
                                            {breadcrumbs[breadcrumbs.length - 1].type === "container" && breadcrumbs[breadcrumbs.length - 1].containerId && (
                                                <button
                                                    onClick={() => navigate(`/shipper/containers/${breadcrumbs[breadcrumbs.length - 1].containerId}`, { state: navigationStateFrom(location) })}
                                                    className="text-tertiary hover:text-primary transition-colors"
                                                    title={t("drive.goToContainer", { defaultValue: "Go to Container" })}
                                                >
                                                    <Package className="size-4" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-4">
                                <Toggle
                                    size="sm"
                                    label={t("drive.showEmptyFolders")}
                                    isSelected={showEmptyFolders}
                                    onChange={(isSelected) => {
                                        setShowEmptyFolders(isSelected);
                                        // Note: Persistence handled by useEffect + folderPrefs hook
                                    }}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        color={viewMode === "card" ? "primary" : "secondary"}
                                        size="sm"
                                        onClick={() => setViewMode("card")}
                                        aria-label={t("drive.view.card")}
                                    >
                                        <LayoutGrid01 className="size-4" />
                                    </Button>
                                    <Button
                                        color={viewMode === "list" ? "primary" : "secondary"}
                                        size="sm"
                                        onClick={() => setViewMode("list")}
                                        aria-label={t("drive.view.list")}
                                    >
                                        <List className="size-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Upload Zone - Only show when inside a custom folder (NOT container folders) */}
                        {!isSearching && currentFolderId && folderDetail && folderDetail.folder_type === "custom" && (
                            <FileUploadDropZone
                                hint={t("drive.upload.dropZone")}
                                onDropFiles={(files) => handleFilesSelected(files)}
                                allowsMultiple={true}
                            />
                        )}

                        {/* Files */}
                        {isSearching ? (
                            <div className="space-y-8">
                                <h2 className="text-lg font-semibold text-primary">
                                    {t("drive.search.results")}
                                </h2>
                                {searchResults.length === 0 ? (
                                    <EmptyState size="md" className="py-12">
                                        <EmptyState.Header>
                                            <EmptyState.FeaturedIcon icon={SearchLg} color="gray" theme="modern" />
                                        </EmptyState.Header>
                                        <EmptyState.Content>
                                            <EmptyState.Title>{t("drive.empty.search.title")}</EmptyState.Title>
                                            <EmptyState.Description>{t("drive.empty.search.description")}</EmptyState.Description>
                                        </EmptyState.Content>
                                    </EmptyState>
                                ) : (
                                    <>
                                        {(searchSectionFolders.containers.length > 0 || searchSectionFolders.customFolders.length > 0) && (
                                            <section>
                                                <h3 className="text-sm font-medium text-secondary mb-3">{t("drive.search.section.folders")}</h3>
                                                <DocumentList
                                                    containers={searchSectionFolders.containers}
                                                    customFolders={searchSectionFolders.customFolders}
                                                    documents={[]}
                                                    viewMode={viewMode}
                                                    containerDocumentCounts={containerDocumentCounts}
                                                    customFolderDocumentCounts={customFolderDocumentCounts}
                                                    containerUpdatedAt={containerUpdatedAt}
                                                    customFolderUpdatedAt={customFolderUpdatedAt}
                                                    containerCreatedAt={containerCreatedAt}
                                                    customFolderCreatedAt={customFolderCreatedAt}
                                                    folderSortField={folderSortField}
                                                    folderSortDirection={folderSortDirection}
                                                    onFolderSort={handleFolderSort}
                                                    documentSortField={documentSortField}
                                                    documentSortDirection={documentSortDirection}
                                                    onDocumentSort={handleDocumentSort}
                                                    onContainerClick={handleContainerClick}
                                                    onContainerContextMenu={handleContainerContextMenu}
                                                    onCustomFolderClick={handleCustomFolderClick}
                                                    onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                                    onDocumentClick={handleDocumentClick}
                                                    onDocumentContextMenu={handleDocumentContextMenu}
                                                    onVersionSelect={(docId) => navigate(`/shipper/documents/${docId}`, { state: navigationStateFrom(location) })}
                                                />
                                            </section>
                                        )}
                                        {searchSectionDocuments.length > 0 && (
                                            <section>
                                                <h3 className="text-sm font-medium text-secondary mb-3">{t("drive.search.section.documents")}</h3>
                                                <DocumentList
                                                    containers={[]}
                                                    customFolders={[]}
                                                    documents={searchSectionDocuments}
                                                    viewMode={viewMode}
                                                    containerDocumentCounts={containerDocumentCounts}
                                                    customFolderDocumentCounts={customFolderDocumentCounts}
                                                    containerUpdatedAt={containerUpdatedAt}
                                                    customFolderUpdatedAt={customFolderUpdatedAt}
                                                    containerCreatedAt={containerCreatedAt}
                                                    customFolderCreatedAt={customFolderCreatedAt}
                                                    folderSortField={folderSortField}
                                                    folderSortDirection={folderSortDirection}
                                                    onFolderSort={handleFolderSort}
                                                    documentSortField={documentSortField}
                                                    documentSortDirection={documentSortDirection}
                                                    onDocumentSort={handleDocumentSort}
                                                    onContainerClick={handleContainerClick}
                                                    onContainerContextMenu={handleContainerContextMenu}
                                                    onCustomFolderClick={handleCustomFolderClick}
                                                    onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                                    onDocumentClick={handleDocumentClick}
                                                    onDocumentContextMenu={handleDocumentContextMenu}
                                                    onVersionSelect={(docId) => navigate(`/shipper/documents/${docId}`, { state: navigationStateFrom(location) })}
                                                />
                                            </section>
                                        )}
                                        {searchSectionContainers.length > 0 && (
                                            <section>
                                                <h3 className="text-sm font-medium text-secondary mb-3">{t("drive.search.section.containers")}</h3>
                                                <DocumentList
                                                    containers={searchSectionContainers}
                                                    customFolders={[]}
                                                    documents={[]}
                                                    viewMode={viewMode}
                                                    containerDocumentCounts={containerDocumentCounts}
                                                    customFolderDocumentCounts={customFolderDocumentCounts}
                                                    containerUpdatedAt={containerUpdatedAt}
                                                    customFolderUpdatedAt={customFolderUpdatedAt}
                                                    containerCreatedAt={containerCreatedAt}
                                                    customFolderCreatedAt={customFolderCreatedAt}
                                                    folderSortField={folderSortField}
                                                    folderSortDirection={folderSortDirection}
                                                    onFolderSort={handleFolderSort}
                                                    documentSortField={documentSortField}
                                                    documentSortDirection={documentSortDirection}
                                                    onDocumentSort={handleDocumentSort}
                                                    onContainerClick={handleContainerClick}
                                                    onContainerContextMenu={handleContainerContextMenu}
                                                    onCustomFolderClick={handleCustomFolderClick}
                                                    onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                                    onDocumentClick={handleDocumentClick}
                                                    onDocumentContextMenu={handleDocumentContextMenu}
                                                    onVersionSelect={(docId) => navigate(`/shipper/documents/${docId}`, { state: navigationStateFrom(location) })}
                                                />
                                            </section>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div
                                onContextMenu={handleGenericContextMenu}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={isDraggingOver ? "min-h-[400px]" : ""}
                            >
                                {/* Show preview inline if files are selected */}
                                {selectedFiles.length > 0 && (
                                    <FileUploadPreviewInline
                                        files={selectedFiles}
                                        onFilesChange={setSelectedFiles}
                                        onAddFiles={handleAddMoreFiles}
                                        onUpload={handleUpload}
                                        onRemoveFile={handleRemoveFile}
                                        onViewContent={() => setSelectedFiles([])}
                                        isUploading={isUploading}
                                    />
                                )}

                                {/* Loading placeholder */}
                                {(foldersLoading || (currentFolderId && folderDetailLoading)) && selectedFiles.length === 0 && (
                                    <div className="space-y-4 animate-pulse">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {Array.from({ length: viewMode === "list" ? 8 : 4 }).map((_, i) => (
                                                viewMode === "list" ? (
                                                    <div key={i} className="col-span-full flex items-center gap-4 p-3 rounded-lg bg-secondary">
                                                        <div className="size-10 rounded-lg bg-tertiary" />
                                                        <div className="flex-1 space-y-2">
                                                            <div className="h-4 w-48 rounded bg-tertiary" />
                                                            <div className="h-3 w-24 rounded bg-tertiary" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={i} className="p-4 rounded-xl bg-secondary ring-1 ring-tertiary">
                                                        <div className="size-12 rounded-lg bg-tertiary mb-3" />
                                                        <div className="h-4 w-32 rounded bg-tertiary mb-2" />
                                                        <div className="h-3 w-20 rounded bg-tertiary" />
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Show empty state or document list based on content */}
                                {!foldersLoading &&
                                    (!currentFolderId || (!folderDetailLoading && folderDetail !== undefined)) &&
                                    displayItems.containers.length === 0 &&
                                    displayItems.customFolders.length === 0 &&
                                    displayItems.documents.length === 0 &&
                                    selectedFiles.length === 0 ? (
                                    <EmptyState
                                        size="md"
                                        className={`py-12 transition-colors ${isDraggingOver && folderDetail?.folder_type !== "container"
                                            ? "ring-2 ring-brand-600 bg-brand-50 dark:bg-brand-950/20 rounded-lg"
                                            : ""
                                            }`}
                                    >
                                        <EmptyState.Header>
                                            <EmptyState.FileTypeIcon type="folder" theme="solid" />
                                        </EmptyState.Header>
                                        <EmptyState.Content>
                                            <EmptyState.Title>
                                                {currentFolderId
                                                    ? t("drive.empty.folder.title")
                                                    : t("drive.empty.myDrive.title")}
                                            </EmptyState.Title>
                                            <EmptyState.Description>
                                                {currentFolderId
                                                    ? t("drive.empty.folder.description")
                                                    : t("drive.empty.myDrive.description")}
                                            </EmptyState.Description>
                                        </EmptyState.Content>
                                        {/* No footer for container folders - upload/create forbidden */}
                                        {folderDetail?.folder_type !== "container" && (
                                            <EmptyState.Footer>
                                                {!currentFolderId ? (
                                                    <Dropdown.Root>
                                                        <Button
                                                            color="primary"
                                                            size="md"
                                                            iconLeading={<Plus className="size-4" />}
                                                        >
                                                            {t("drive.add.button")}
                                                        </Button>
                                                        <Dropdown.Popover>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item
                                                                    icon={Folder}
                                                                    onAction={() => {
                                                                        setNewFolderName("");
                                                                        setSelectedParentFolderId(null);
                                                                        setShowCreateFolderModal(true);
                                                                    }}
                                                                >
                                                                    {t("drive.add.folder")}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    icon={Upload01}
                                                                    onAction={() => {
                                                                        fileInputRef.current?.click();
                                                                    }}
                                                                >
                                                                    {t("drive.add.files")}
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown.Popover>
                                                    </Dropdown.Root>
                                                ) : folderDetail && folderDetail.folder_type === "custom" ? (
                                                    <Dropdown.Root>
                                                        <Button
                                                            color="primary"
                                                            size="md"
                                                            iconLeading={<Plus className="size-4" />}
                                                        >
                                                            {t("drive.add.button")}
                                                        </Button>
                                                        <Dropdown.Popover>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item
                                                                    icon={Folder}
                                                                    onAction={() => {
                                                                        setNewFolderName("");
                                                                        setSelectedParentFolderId(currentFolderId);
                                                                        setShowCreateFolderModal(true);
                                                                    }}
                                                                >
                                                                    {t("drive.add.folder")}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item
                                                                    icon={Upload01}
                                                                    onAction={() => {
                                                                        fileInputRef.current?.click();
                                                                    }}
                                                                >
                                                                    {t("drive.add.files")}
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown.Popover>
                                                    </Dropdown.Root>
                                                ) : null}
                                            </EmptyState.Footer>
                                        )}
                                    </EmptyState>
                                ) : selectedFiles.length === 0 ? (
                                    <>
                                        {/* Folder Bulk Actions Bar */}
                                        {selectedFolderIds.size > 0 && (displayItems.customFolders.length > 0 || displayItems.containers.length > 0) && (
                                            <div className="mb-4 flex items-center justify-between rounded-lg bg-primary p-3 ring-1 ring-secondary">
                                                <div className="flex items-center gap-4">
                                                    <Checkbox
                                                        isSelected={selectedFolderIds.size === (displayItems.customFolders.length + displayItems.containers.length) && (displayItems.customFolders.length + displayItems.containers.length) > 0}
                                                        onChange={handleSelectAllFolders}
                                                    >
                                                        {selectedFolderIds.size === (displayItems.customFolders.length + displayItems.containers.length)
                                                            ? t("drive.bulkActions.deselectAll")
                                                            : t("drive.bulkActions.selectAll")}
                                                    </Checkbox>
                                                    <span className="text-sm text-tertiary">
                                                        {t("drive.bulkActions.selected", { count: selectedFolderIds.size })}
                                                    </span>
                                                    <Button color="primary" size="sm" onClick={handleBulkMoveFolders} iconLeading={<Move className="size-4" />}>
                                                        {t("drive.folder.moveAction", { defaultValue: "Move" })}
                                                    </Button>
                                                    {allSelectedAreCustomFolders && (
                                                        <Button color="primary-destructive" size="sm" onClick={handleBulkDeleteFolders} iconLeading={<Trash01 className="size-4" />}>
                                                            {t("drive.bulkActions.delete", { defaultValue: "Delete" })}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Document Bulk Actions Bar */}
                                        {selectedDocumentIds.size > 0 && displayItems.documents.length > 0 && (
                                            <div className="mb-4 flex items-center justify-between rounded-lg bg-primary p-3 ring-1 ring-secondary">
                                                <div className="flex items-center gap-4">
                                                    <Checkbox
                                                        isSelected={selectedDocumentIds.size === displayItems.documents.length && displayItems.documents.length > 0}
                                                        onChange={handleSelectAllDocuments}
                                                    >
                                                        {selectedDocumentIds.size === displayItems.documents.length
                                                            ? t("drive.bulkActions.deselectAll")
                                                            : t("drive.bulkActions.selectAll")}
                                                    </Checkbox>
                                                    <span className="text-sm text-tertiary">
                                                        {t("drive.bulkActions.selected", { count: selectedDocumentIds.size })}
                                                    </span>
                                                    <Button color="primary" size="sm" onClick={handleBulkDelete} iconLeading={<Trash01 className="size-4" />}>
                                                        {t("drive.bulkActions.delete")}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <DocumentList
                                            containers={displayItems.containers}
                                            customFolders={displayItems.customFolders}
                                            documents={displayItems.documents}
                                            viewMode={viewMode}
                                            selectedItems={[
                                                ...Array.from(selectedDocumentIds),
                                                ...Array.from(selectedFolderIds).map(id =>
                                                    displayItems.containers.some(c => c.id === id) ? `container-${id}` : `folder-${id}`
                                                ),
                                            ]}
                                            containerDocumentCounts={containerDocumentCounts}
                                            customFolderDocumentCounts={customFolderDocumentCounts}
                                            containerUpdatedAt={containerUpdatedAt}
                                            customFolderUpdatedAt={customFolderUpdatedAt}
                                            containerCreatedAt={containerCreatedAt}
                                            customFolderCreatedAt={customFolderCreatedAt}
                                            folderSortField={folderSortField}
                                            folderSortDirection={folderSortDirection}
                                            onFolderSort={handleFolderSort}
                                            documentSortField={documentSortField}
                                            documentSortDirection={documentSortDirection}
                                            onDocumentSort={handleDocumentSort}
                                            onContainerClick={handleContainerClick}
                                            onContainerContextMenu={handleContainerContextMenu}
                                            onContainerCheckboxChange={handleContainerFolderSelect}
                                            onCustomFolderClick={handleCustomFolderClick}
                                            onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                            onDocumentClick={handleDocumentClick}
                                            onDocumentContextMenu={handleDocumentContextMenu}
                                            onDocumentCheckboxChange={handleDocumentSelect}
                                            onCustomFolderCheckboxChange={handleFolderSelect}
                                            onVersionSelect={(docId) => navigate(`/shipper/documents/${docId}`, { state: navigationStateFrom(location) })}
                                        />
                                    </>
                                ) : null}

                                {/* Hidden file input for upload - always render */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                />
                            </div>
                        )}
                    </div>
                </Tabs.Panel>

                {/* Unmapped Files Tab */}
                <Tabs.Panel id="unmapped">
                    <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                color={viewMode === "card" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => setViewMode("card")}
                                aria-label={t("drive.view.card")}
                            >
                                <LayoutGrid01 className="size-4" />
                            </Button>
                            <Button
                                color={viewMode === "list" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => setViewMode("list")}
                                aria-label={t("drive.view.list")}
                            >
                                <List className="size-4" />
                            </Button>
                        </div>

                        {/* Bulk Actions Bar */}
                        {selectedDocumentIds.size > 0 && unmappedAsDocuments.length > 0 && (
                            <div className="mb-4 flex items-center justify-between rounded-lg bg-primary p-3 ring-1 ring-secondary">
                                <div className="flex items-center gap-4">
                                    <Checkbox
                                        isSelected={selectedDocumentIds.size === unmappedAsDocuments.length && unmappedAsDocuments.length > 0}
                                        onChange={() => {
                                            if (selectedDocumentIds.size === unmappedAsDocuments.length) {
                                                setSelectedDocumentIds(new Set());
                                            } else {
                                                setSelectedDocumentIds(new Set(unmappedAsDocuments.map(d => d.id)));
                                            }
                                        }}
                                    >
                                        {selectedDocumentIds.size === unmappedAsDocuments.length
                                            ? t("drive.bulkActions.deselectAll")
                                            : t("drive.bulkActions.selectAll")}
                                    </Checkbox>
                                    <span className="text-sm text-tertiary">
                                        {t("drive.bulkActions.selected", { count: selectedDocumentIds.size })}
                                    </span>
                                    <Button color="primary" size="sm" onClick={() => setShowMoveModal(true)} iconLeading={<Move className="size-4" />}>
                                        {t("drive.unmapped.bulkActions.associate")}
                                    </Button>
                                    <Button color="primary-destructive" size="sm" onClick={handleBulkDelete} iconLeading={<Trash01 className="size-4" />}>
                                        {t("drive.bulkActions.delete")}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Files */}
                        {unmappedAsDocuments.length === 0 ? (
                            <EmptyState size="md" className="py-12">
                                <EmptyState.Header>
                                    <EmptyState.FileTypeIcon type="file" theme="solid" />
                                </EmptyState.Header>
                                <EmptyState.Content>
                                    <EmptyState.Title>{t("drive.unmapped.empty.title")}</EmptyState.Title>
                                    <EmptyState.Description>{t("drive.unmapped.empty.message")}</EmptyState.Description>
                                </EmptyState.Content>
                            </EmptyState>
                        ) : (
                            <DocumentList
                                documents={unmappedAsDocuments}
                                viewMode={viewMode}
                                selectedItems={Array.from(selectedDocumentIds)}
                                onDocumentClick={handleDocumentClick}
                                onDocumentContextMenu={handleDocumentContextMenu}
                                onDocumentCheckboxChange={handleDocumentSelect}
                            />
                        )}
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Context Menu */}
            <ContextMenu
                isOpen={contextMenu.isOpen}
                onClose={closeContextMenu}
                position={contextMenu.position}
                items={contextMenu.items}
            />

            {/* Rename Modal */}
            {showRenameModal && (
                <ModalOverlay isOpen={showRenameModal} onOpenChange={setShowRenameModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {selectedDocument
                                    ? t("drive.document.rename.title")
                                    : t("drive.folder.rename")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={selectedDocument ? t("drive.document.rename.label") : t("drive.folder.rename")}
                                    value={renameValue}
                                    onChange={setRenameValue}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowRenameModal(false);
                                        setSelectedDocument(null);
                                        setSelectedCustomFolder(null);
                                        setRenameValue("");
                                    }}>
                                        {selectedDocument ? t("drive.document.rename.cancel") : t("drive.document.rename.cancel")}
                                    </Button>
                                    <Button onClick={handleRename}>
                                        {selectedDocument ? t("drive.document.rename.save") : t("drive.document.rename.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <ModalOverlay isOpen={showDeleteModal} onOpenChange={setShowDeleteModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {selectedDocument
                                    ? t("drive.document.delete.title")
                                    : t("drive.folder.delete")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">
                                    {selectedDocument
                                        ? t("drive.document.delete.message")
                                        : t("drive.folder.cannotDelete")}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedDocument(null);
                                        setSelectedCustomFolder(null);
                                    }}>
                                        {selectedDocument ? t("drive.document.delete.cancel") : t("drive.document.delete.cancel")}
                                    </Button>
                                    <Button color="primary-destructive" onClick={handleDelete}>
                                        {selectedDocument ? t("drive.document.delete.confirm") : t("drive.folder.delete")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Classify Modal */}
            {showClassifyModal && selectedDocument && (
                <ModalOverlay isOpen={showClassifyModal} onOpenChange={setShowClassifyModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.classify.title", { defaultValue: "Classify Document" })}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-sm text-tertiary mb-2">
                                    {t("drive.document.classify.description", {
                                        defaultValue: "Select the document type to manually classify this document."
                                    })}
                                </p>
                                <Select
                                    label={t("drive.document.classify.label", { defaultValue: "Document Type" })}
                                    selectedKey={selectedDocumentType}
                                    onSelectionChange={(key) => setSelectedDocumentType(key as string)}
                                    items={documentTypesData?.map(dt => ({
                                        id: dt.key,
                                        label: dt.name
                                    })) || []}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowClassifyModal(false);
                                        setSelectedDocument(null);
                                        setSelectedDocumentType("");
                                    }}>
                                        {t("common:cancel", { defaultValue: "Cancel" })}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedDocument && selectedDocumentType) {
                                                updateDocumentMutation.mutate({
                                                    documentId: selectedDocument.id,
                                                    data: { document_type: selectedDocumentType }
                                                }, {
                                                    onSuccess: () => {
                                                        setShowClassifyModal(false);
                                                        setSelectedDocument(null);
                                                        setSelectedDocumentType("");
                                                        // Refresh folder documents
                                                        queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.all });
                                                    }
                                                });
                                            }
                                        }}
                                        disabled={!selectedDocumentType || updateDocumentMutation.isPending}
                                    >
                                        {updateDocumentMutation.isPending
                                            ? t("common:saving", { defaultValue: "Saving..." })
                                            : t("common:save", { defaultValue: "Save" })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Move Modal */}
            {showMoveModal && (
                <ModalOverlay isOpen={showMoveModal} onOpenChange={setShowMoveModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.move.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Select
                                    label={t("drive.document.move.label")}
                                    selectedKey={selectedContainerId}
                                    onSelectionChange={(key) => setSelectedContainerId(key as string)}
                                    items={containerSelectItems}
                                    placeholder={containerSelectItems.length === 0 ? "Loading containers..." : "Select a container"}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowMoveModal(false);
                                        setSelectedDocument(null);
                                        setSelectedContainerId("");
                                    }}>
                                        {t("drive.document.move.cancel")}
                                    </Button>
                                    <Button
                                        onClick={handleMove}
                                        isDisabled={!selectedContainerId || updateDocumentMutation.isPending}
                                        isLoading={updateDocumentMutation.isPending}
                                    >
                                        {t("drive.document.move.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Share Modal */}
            {showShareModal && selectedDocument && (
                <ModalOverlay isOpen={showShareModal} onOpenChange={setShowShareModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.share.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.document.share.email")}
                                    type="email"
                                    placeholder="email@example.com"
                                />
                                <Select
                                    label={t("drive.document.share.permission")}
                                    selectedKey="view"
                                    items={[
                                        { id: "view", label: t("drive.document.share.view") },
                                        { id: "edit", label: t("drive.document.share.edit") },
                                        { id: "comment", label: t("drive.document.share.comment") },
                                    ]}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowShareModal(false);
                                        setSelectedDocument(null);
                                    }}>
                                        {t("drive.document.share.cancel")}
                                    </Button>
                                    <Button onClick={handleShare}>
                                        {t("drive.document.share.send")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Download Modal */}
            {showDownloadModal && selectedDocument && (
                <ModalOverlay isOpen={showDownloadModal} onOpenChange={setShowDownloadModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.detail.downloadConfirm.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">
                                    {t("drive.detail.downloadConfirm.message")}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowDownloadModal(false);
                                        setSelectedDocument(null);
                                    }}>
                                        {t("drive.detail.downloadConfirm.cancel")}
                                    </Button>
                                    <Button onClick={handleDownload}>
                                        {t("drive.detail.downloadConfirm.download")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Move Folder Modal */}
            {showMoveFolderModal && (selectedCustomFolder || selectedContainer || selectedFolderIds.size > 0) && (
                <ModalOverlay isOpen={showMoveFolderModal} onOpenChange={(open) => {
                    setShowMoveFolderModal(open);
                    if (!open) {
                        setIsCreatingInlineFolder(false);
                        setInlineNewFolderName("");
                    }
                }}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg w-full mx-auto flex flex-col">
                            <div className="flex flex-col gap-4 w-full">
                                <h2 className="text-lg font-semibold text-primary mb-4">
                                    {t("drive.folder.move.title")}
                                </h2>
                                <div className="space-y-4 w-full">
                                    <Select
                                        label={t("drive.folder.move.label")}
                                        selectedKey={selectedParentFolderId === null ? "root" : selectedParentFolderId}
                                        onSelectionChange={(key) => {
                                            setSelectedParentFolderId(key === "root" ? null : key as string);
                                            // Dismiss inline creation when user picks from dropdown
                                            if (isCreatingInlineFolder) {
                                                setIsCreatingInlineFolder(false);
                                                setInlineNewFolderName("");
                                            }
                                        }}
                                        items={getAvailableFoldersForMove().map(f => ({ id: f.id === null ? "root" : f.id, label: f.label }))}
                                    >
                                        {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                    </Select>

                                    {/* Inline new folder creation */}
                                    {isCreatingInlineFolder ? (
                                        <div className="flex items-end gap-2">
                                            <div className="flex-1">
                                                <Input
                                                    label={t("drive.folder.createModal.label", { defaultValue: "New folder name" })}
                                                    value={inlineNewFolderName}
                                                    onChange={setInlineNewFolderName}
                                                    autoFocus
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="mb-1 p-1.5 text-tertiary hover:text-primary rounded-md hover:bg-secondary"
                                                onClick={() => { setIsCreatingInlineFolder(false); setInlineNewFolderName(""); }}
                                            >
                                                <XClose className="size-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                                            onClick={() => setIsCreatingInlineFolder(true)}
                                        >
                                            <Plus className="size-4" />
                                            {t("drive.folder.move.createNew", { defaultValue: "Create new folder" })}
                                        </button>
                                    )}

                                    <div className="flex justify-end gap-2">
                                        <Button color="secondary" onClick={() => {
                                            setShowMoveFolderModal(false);
                                            setSelectedCustomFolder(null);
                                            setSelectedContainer(null);
                                            setSelectedParentFolderId(null);
                                            setSelectedFolderIds(new Set());
                                            setIsCreatingInlineFolder(false);
                                            setInlineNewFolderName("");
                                        }}>
                                            {t("drive.folder.move.cancel")}
                                        </Button>
                                        <Button
                                            onClick={handleMoveFolder}
                                            isDisabled={isMovingFolder}
                                            isLoading={isMovingFolder}
                                        >
                                            {t("drive.folder.move.save")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Create Folder Modal */}
            {showCreateFolderModal && (
                <ModalOverlay isOpen={showCreateFolderModal} onOpenChange={setShowCreateFolderModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.folder.createModal.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.folder.createModal.label")}
                                    value={newFolderName}
                                    onChange={setNewFolderName}
                                />
                                <Select
                                    label={t("drive.folder.createModal.parentLabel")}
                                    selectedKey={selectedParentFolderId === null ? "root" : selectedParentFolderId}
                                    onSelectionChange={(key) => setSelectedParentFolderId(key === "root" ? null : key as string)}
                                    items={[
                                        { id: "root", label: t("drive.folder.move.root") },
                                        ...(foldersData?.filter(f => f.folder_type === "custom") || []).map(f => ({
                                            id: f.folder_id,
                                            label: f.name || "",
                                        }))
                                            .filter(f => f.id !== selectedParentFolderId)
                                    ]}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowCreateFolderModal(false);
                                        setNewFolderName("");
                                        setSelectedParentFolderId(null);
                                    }}>
                                        {t("drive.folder.createModal.cancel")}
                                    </Button>
                                    <Button onClick={handleCreateFolder}>
                                        {t("drive.folder.createModal.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Folders Confirmation Modal (non-empty folders warning) */}
            {showDeleteFoldersModal && (
                <ModalOverlay isOpen={showDeleteFoldersModal} onOpenChange={(open) => {
                    if (!isDeletingFolders) setShowDeleteFoldersModal(open);
                }}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.folder.delete", { defaultValue: "Delete Folders" })}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">
                                    {t("drive.folder.bulkDelete.warning", {
                                        defaultValue: `The selected folders contain {{count}} document(s). Deleting these folders will remove the document associations and they may become unmapped.`,
                                        count: selectedFoldersTotalDocCount,
                                    })}
                                </p>
                                <p className="text-sm font-medium text-warning-600">
                                    {t("drive.folder.bulkDelete.confirm", { defaultValue: "Are you sure you want to proceed?" })}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => setShowDeleteFoldersModal(false)} isDisabled={isDeletingFolders}>
                                        {t("drive.document.delete.cancel", { defaultValue: "Cancel" })}
                                    </Button>
                                    <Button
                                        color="primary-destructive"
                                        onClick={executeBulkDeleteFolders}
                                        isDisabled={isDeletingFolders}
                                        isLoading={isDeletingFolders}
                                    >
                                        {t("drive.folder.delete", { defaultValue: "Delete" })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Create Version Modal */}
            {showCreateVersionModal && selectedDocument && (
                <ModalOverlay isOpen={showCreateVersionModal} onOpenChange={setShowCreateVersionModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.createVersion.title", { defaultValue: "Create New Version" })}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-sm text-tertiary">
                                    {t("drive.document.createVersion.description", {
                                        defaultValue: "Upload a new file to create a new version of this document.",
                                        documentName: selectedDocument.name
                                    })}
                                </p>
                                <div className="text-sm text-secondary bg-secondary/50 rounded-lg p-3">
                                    <p className="font-medium">{t("drive.document.createVersion.currentFile", { defaultValue: "Current file:" })}</p>
                                    <p className="text-tertiary truncate">{selectedDocument.name}</p>
                                </div>

                                {/* File selection */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-secondary">
                                        {t("drive.document.createVersion.newFile", { defaultValue: "New version file" })}
                                    </label>
                                    <div
                                        {...getRootProps()}
                                        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${isDragActive
                                            ? "border-brand-500 bg-brand-50 dark:bg-brand-950"
                                            : "border-secondary hover:border-brand"
                                            }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            open();
                                        }}
                                    >
                                        <input {...getInputProps()} />
                                        {versionFile ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <Upload01 className="w-4 h-4 text-brand" />
                                                <span className="text-sm text-primary truncate">{versionFile.name}</span>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2">
                                                <Upload01 className="w-6 h-6 text-tertiary" />
                                                <span className="text-sm text-tertiary">
                                                    {isDragActive
                                                        ? t("drive.document.createVersion.dropHere", { defaultValue: "Drop file here!" })
                                                        : t("drive.document.createVersion.dropFile", { defaultValue: "Click to select file" })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowCreateVersionModal(false);
                                        setSelectedDocument(null);
                                        setVersionFile(null);
                                    }}>
                                        {t("common:cancel", { defaultValue: "Cancel" })}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (selectedDocument && versionFile) {
                                                createVersionMutation.mutate({
                                                    documentId: selectedDocument.id,
                                                    file: versionFile
                                                }, {
                                                    onSuccess: (newDoc) => {
                                                        toast.success(t("drive.document.createVersion.success", {
                                                            defaultValue: "New version created successfully"
                                                        }));
                                                        setShowCreateVersionModal(false);
                                                        setSelectedDocument(null);
                                                        setVersionFile(null);
                                                        // Invalidate queries to refresh
                                                        if (currentFolderId) {
                                                            queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.documents(currentFolderId) });
                                                            queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.detail(currentFolderId) });
                                                        }
                                                        // Navigate to new version
                                                        if (newDoc?.document_id) {
                                                            navigate(`/shipper/documents/${newDoc.document_id}`, { state: navigationStateFrom(location) });
                                                        }
                                                    },
                                                    onError: (error: any) => {
                                                        toast.error(t("drive.document.createVersion.error", {
                                                            defaultValue: `Failed to create version: ${error?.message || "Unknown error"}`
                                                        }));
                                                    }
                                                });
                                            }
                                        }}
                                        disabled={!versionFile || createVersionMutation.isPending}
                                    >
                                        {createVersionMutation.isPending
                                            ? t("common:uploading", { defaultValue: "Uploading..." })
                                            : t("drive.document.createVersion.create", { defaultValue: "Create Version" })}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

